import { randomDelay } from "@/lib/utils/sleep";
import type { AuthUser } from "../stores/authStore";

type Provider = "google" | "microsoft" | "apple";

const VALID_EMAIL = "carlos@imobgo.local";
const VALID_PASSWORD = "admin@123";

const mockUser: AuthUser = {
  id: "broker_01J9GZ7WCFVK7K8GZ5QF6PY3JN",
  name: "Carlos Ferreira",
  email: VALID_EMAIL,
  role: "agent",
  brokerId: "broker_01J9GZ7WCFVK7K8GZ5QF6PY3JN",
  authProvider: "local",
  emailVerified: true,
};

function fakeToken(prefix = "access") {
  return `mock.${prefix}.${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export async function loginWithCredentials(input: { email: string; password: string; turnstileToken?: string }) {
  await randomDelay(400, 900);
  if (input.email.trim().toLowerCase() !== VALID_EMAIL || input.password !== VALID_PASSWORD) {
    const err = new Error("INVALID_CREDENTIALS") as Error & { code: string };
    err.code = "INVALID_CREDENTIALS";
    throw err;
  }
  return {
    accessToken: fakeToken("access"),
    user: { ...mockUser, email: input.email.trim().toLowerCase() },
  };
}

export async function loginWithProvider(
  provider: Provider,
  _payload?: { idToken?: string; code?: string; redirectUri?: string; codeVerifier?: string },
) {
  await randomDelay(500, 1100);
  return {
    accessToken: fakeToken(`access-${provider}`),
    user: {
      ...mockUser,
      authProvider: provider,
      name:
        provider === "google"
          ? "Carlos (via Google)"
          : provider === "microsoft"
            ? "Carlos (via Microsoft)"
            : "Carlos (via Apple)",
    },
  };
}

export async function activateAccount(token: string, _password: string) {
  await randomDelay(500, 1000);
  if (!token || token.trim().length < 8) {
    const err = new Error("INVALID_TOKEN") as Error & { code: string };
    err.code = "INVALID_TOKEN";
    throw err;
  }
  // Conta recém-ativada: cai no onboarding (perfil/termos pendentes).
  return {
    accessToken: fakeToken("access"),
    user: { ...mockUser, profileCompleted: false, termsAccepted: false },
  };
}

export async function getPublicBrokerInvite(token: string) {
  await randomDelay(300, 600);
  if (!token || token.trim().length < 8) {
    const err = new Error("INVALID_TOKEN") as Error & { code: string };
    err.code = "INVALID_TOKEN";
    throw err;
  }
  return {
    inviterName: "Imobiliária Exemplo",
    email: "convidado@exemplo.com",
    status: "pending" as const,
  };
}

export async function requestPasswordReset(email: string, _turnstileToken?: string) {
  await randomDelay(400, 700);
  return { ok: true, email };
}

export async function resetPassword(token: string, newPassword: string, _turnstileToken?: string) {
  await randomDelay(400, 700);
  return { ok: true, token, newPassword };
}

export async function verifyEmail(token: string) {
  await randomDelay(400, 800);
  if (!token || token.trim().length < 6) {
    const err = new Error("INVALID_TOKEN") as Error & { code: string };
    err.code = "INVALID_TOKEN";
    throw err;
  }
  return { ok: true };
}

export async function resendVerificationEmail() {
  await randomDelay(400, 700);
  return { ok: true };
}

export async function refresh() {
  await randomDelay(200, 400);
  return {
    accessToken: fakeToken("access"),
    user: { ...mockUser },
  };
}

export async function logout() {
  await randomDelay(150, 250);
}
